import {
  GameHandler,
  CardModalData,
  DeckBuilderCardFunctions,
} from "@/interfaces/GameHandler";
import { CardItem, UAItem } from "@/interfaces/CardItem";
import {
  getCardImage,
  getCardData,
  getUAIdFromTcgPlayerId,
  getAltArts,
  getAACardId,
  getAACardIdData,
  getAAFromCardId,
  cleanCardData,
} from "@/lib/games/unionArena/getCardDataLocal";
import { getArchetypeDataUA } from "@/lib/games/unionArena/archetypeManager";
import type { DeckIdentity } from "@/interfaces/DeckIdentity";
import { SrcType } from "@/models/alt-arts";
import { getTCGPlayerCardImage } from "@/lib/tcgplayer/tcgplayer_helper";
import { replaceKeywords } from "@/lib/games/unionArena/keywords";
import getDefaultFilters from "@/components/DeckBuilder/game/unionArena/filters/getDefaultFilters";
import { filterCards as filterCardsUA } from "@/components/DeckBuilder/game/unionArena/filters/filterCards";
import { DeckCardsMap } from "@/lib/games/onePiece/DeckCommonCardsFilter";
import { sortCards as uaSortCards } from "@/lib/games/unionArena/deckbuilderSortCards";
import { CARDS_PER_PAGE } from "@/components/DeckBuilder/config";
import { getImgproxyUrl } from "@/lib/imgproxy";
import { gameAssetUrl } from "@/lib/cdn";
import { getFullCardId } from "@/lib/games/unionArena/getCardDataLocal";

export class UAHandler implements GameHandler {
  getSetImage(set: string): string {
    // Use CDN directly without imgproxy for set squares
    return gameAssetUrl("union-arena", "set-squares", `${set}.webp`, "1.0.1");
  }

  getCardImage(cardId: string, size: string, src: SrcType | "" = ""): string {
    // size/src were previously dropped, so every card came back full-resolution.
    return getCardImage(cardId, size as any, src);
  }

  getTcgPlayerCardData(tcgplayerId: string): CardItem | undefined {
    const UAId = getUAIdFromTcgPlayerId(tcgplayerId);
    console.log("UAId", UAId);
    if (UAId && UAId !== "") {
      return getCardData(UAId);
    }
    return undefined;
  }

  getCardData(cardId: string): CardItem | undefined {
    return getCardData(cardId);
  }

  getLeaderData(leaderId: string): DeckIdentity | undefined {
    return getArchetypeDataUA(leaderId);
  }

  hasLeader(): boolean {
    return false;
  }

  sortDecklist(cardList: string, leader?: string): string {
    // card list is of the form 4xSAO-2-032;4xSAO-2-033;...4xSAO-2-061
    // need to sort by energy_cost, ap_cost, battle_power, name

    const cards = cardList.split(";");

    // combine duplicates like aa arts
    const cardMap = new Map<string, number>();
    for (const card of cards) {
      const [count, cardId] = card.split("x");
      cardMap.set(cardId, (cardMap.get(cardId) || 0) + parseInt(count));
    }

    // Convert cardMap to array of [cardId, count] pairs for sorting
    const cardArray = Array.from(cardMap.entries()).map(([cardId, count]) => {
      const cardData = getCardData(cardId);
      return {
        cardId,
        count,
        cardData: cardData as UAItem | undefined,
      };
    });

    // Sort the array based on the specified criteria
    cardArray.sort((a, b) => {
      if (!a.cardData || !b.cardData) return 0;

      // Sort by card_type first
      const typeOrder = ["Character", "Event", "Site", "Field"];
      const aTypeIndex = typeOrder.indexOf(a.cardData.card_type);
      const bTypeIndex = typeOrder.indexOf(b.cardData.card_type);
      if (aTypeIndex !== bTypeIndex) {
        return aTypeIndex - bTypeIndex;
      }

      // Sort by energy_cost
      const aEnergyCost = parseInt(a.cardData.energy_cost) || 0;
      const bEnergyCost = parseInt(b.cardData.energy_cost) || 0;
      if (aEnergyCost !== bEnergyCost) {
        return aEnergyCost - bEnergyCost;
      }

      // Sort by ap_cost
      const aApCost = parseInt(a.cardData.ap_cost) || 0;
      const bApCost = parseInt(b.cardData.ap_cost) || 0;
      if (aApCost !== bApCost) {
        return aApCost - bApCost;
      }

      // Sort by battle_power
      const aBattlePower = a.cardData.battle_power || "0";
      const bBattlePower = b.cardData.battle_power || "0";

      // Extract numeric part and suffix
      const aMatch = aBattlePower.match(/^(\d+)([+-]*)$/);
      const bMatch = bBattlePower.match(/^(\d+)([+-]*)$/);

      const aNum = aMatch ? parseInt(aMatch[1]) : 0;
      const bNum = bMatch ? parseInt(bMatch[1]) : 0;

      if (aNum !== bNum) {
        return aNum - bNum;
      }

      // If numbers are equal, sort by suffix
      const aSuffix = aMatch ? aMatch[2] : "";
      const bSuffix = bMatch ? bMatch[2] : "";
      const suffixOrder = ["", "-", "+", "+-"];
      const aSuffixIndex = suffixOrder.indexOf(aSuffix);
      const bSuffixIndex = suffixOrder.indexOf(bSuffix);
      if (aSuffixIndex !== bSuffixIndex) {
        return aSuffixIndex - bSuffixIndex;
      }

      // Sort by name
      return a.cardData.name.localeCompare(b.cardData.name);
    });

    // Convert back to the original format
    return cardArray.map(({ cardId, count }) => `${count}x${cardId}`).join(";");
  }

  /** Union Arena decklists are exported with the set-qualified card id. */
  getExportCardId(cardId: string): string | null {
    return getFullCardId(cardId) || cardId;
  }

  getSet(card: CardItem): string {
    return (card as UAItem).card_set || "";
  }

  getTraits(card: CardItem): string[] {
    const affinity = (card as UAItem).affinity;
    return affinity ? [affinity] : [];
  }

  getCardModalData(): CardModalData {
    return {
      getCardImage: (cardId: string, tcgplayerId?: string | null) => {
        if (tcgplayerId) {
          return getTCGPlayerCardImage(tcgplayerId);
        }
        return getCardImage(cardId);
      },
      getCardType: (card: CardItem) => {
        return (card as UAItem).card_type;
      },
      getCardEffect: (card: CardItem) => {
        return replaceKeywords((card as UAItem).effect ?? "");
      },
      getCardTrigger: (card: CardItem) => {
        return replaceKeywords((card as UAItem).trigger ?? "", false, true);
      },
      getCardLink: (card: CardItem, tcgplayerId?: string | null) => {
        return tcgplayerId
          ? `https://www.tcgplayer.com/product/${tcgplayerId}`
          : "#";
      },
      getAdditionalChips: (card: CardItem) => {
        const uaCard = card as UAItem;
        const chips: string[] = [];
        if (uaCard.affinity !== "") {
          chips.push(uaCard.affinity);
        }
        return chips;
      },
    };
  }

  // DeckBuilder-specific methods
  getDeckBuilderCardFunctions(): DeckBuilderCardFunctions {
    return {
      getCardData,
      getAltArts,
      getAACardId,
      getAACardIdData,
      getAAFromCardId,
      cleanCardData,
    };
  }

  getDefaultFilters() {
    return getDefaultFilters();
  }

  getCardsPerPage(): number {
    return CARDS_PER_PAGE;
  }

  getModalCardData(aaCardId: string): CardItem | undefined {
    return aaCardId ? getCardData(aaCardId) : undefined;
  }

  filterCards(
    cards: CardItem[],
    filters: any,
    defaultFilters: any,
    deckCardsMap: DeckCardsMap,
  ): CardItem[] {
    return filterCardsUA(
      cards as UAItem[],
      filters,
      defaultFilters,
      deckCardsMap,
    );
  }

  // Optional game-specific methods
  getEnergyCost(card: CardItem): number {
    return parseInt((card as UAItem).energy_cost) || 0;
  }

  getColor(card: CardItem): string {
    return (card as UAItem).color;
  }

  sortCards(a: CardItem, b: CardItem): number {
    return uaSortCards(a as UAItem, b as UAItem);
  }
}
